import React, { useCallback, useEffect, useRef, useState } from 'react';
import API from '../../api/api';
import { ClipCostSummary } from '../../api/clip';
import { ClipPrompt } from '../../api/structs/clip';
import { Account, User } from '../../api/structs/user';
import { MediaProfile } from '../../api/structs/media-spec';
import { useRenderPollingFallback } from '../../hooks';
import ClipPromptItem from './ClipPromptItem';

interface ClipPromptsListProps {
  refreshTrigger: number;
  mediaProfile: MediaProfile;
  activeAccount: Account | null;
  activeUser: User | null;
  onOpenEditor?: (clipId: string) => void;
  /** A clip to open, scroll to and focus once the list has it (a finished run's manifest clip, a rail click, a ?clip= deep link). */
  focusClipId?: string;
  /** Changes on every "open this clip" request, so asking for the same clip again re-opens it. */
  focusRequest?: number;
}

const ClipPromptsList: React.FC<ClipPromptsListProps> = ({
  refreshTrigger,
  mediaProfile,
  activeAccount,
  activeUser,
  onOpenEditor,
  focusClipId,
  focusRequest,
}) => {
  const [clips, setClips] = useState<ClipPrompt[]>([]);
  const [costByClip, setCostByClip] = useState<Record<string, ClipCostSummary>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [hydratingId, setHydratingId] = useState<string | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchClips = useCallback(async () => {
    try {
      const { clips: fetched, costByClip: costs } = await API.getClipPrompts();
      setClips(fetched);
      setCostByClip(costs);
      setFetchError(null);
    } catch (error) {
      console.error('Failed to fetch clips:', error);
      setFetchError('Couldn\'t load clips.');
    } finally {
      setIsInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClips();
  }, [refreshTrigger, fetchClips]);

  // A stub row is served straight from the durable store because the backend
  // evicted the clip from memory — its media only arrives once it is hydrated.
  const handleToggleExpand = useCallback(
    async (clip: ClipPrompt) => {
      if (expandedId === clip.id) {
        setExpandedId(null);
        return;
      }
      setExpandedId(clip.id);
      if (!clip.is_stub || hydratingId === clip.id) return;
      setHydratingId(clip.id);
      try {
        await API.hydrateClip(clip.id);
        await fetchClips();
      } catch (error) {
        console.error('Failed to load clip from the archive:', error);
      } finally {
        setHydratingId(null);
      }
    },
    [expandedId, hydratingId, fetchClips]
  );

  // Applied once per request: the user can close the clip again without the
  // next refresh re-opening it, and a new request for the same clip re-opens it.
  const appliedFocusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusClipId) return;
    const requestKey = `${focusClipId}\u0000${focusRequest ?? ''}`;
    if (appliedFocusRef.current === requestKey) return;
    const target = clips.find((candidate) => candidate.id === focusClipId);
    if (!target) return;
    appliedFocusRef.current = requestKey;
    if (expandedId !== focusClipId) void handleToggleExpand(target);
    // The id comes from a URL (?clip=) as well as from the backend: escape it
    // before it becomes part of a selector (Ruling 14). jsdom's test
    // environment implements CSS.escape too.
    const wrapper = document.querySelector<HTMLElement>(`[data-clip-id="${CSS.escape(focusClipId)}"]`);
    wrapper?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    // The control that asked (a rail row, "Open carousel") is gone or elsewhere:
    // move keyboard and screen-reader focus to the clip it opened.
    wrapper?.focus({ preventScroll: true });
  }, [clips, focusClipId, focusRequest, expandedId, handleToggleExpand]);

  const focusMissing =
    Boolean(focusClipId) && !isInitialLoading && !fetchError && !clips.some((candidate) => candidate.id === focusClipId);

  // Fallback for when live "RenderProgress" WS pushes are lost in transport:
  // poll the clips list while any clip is rendering so fresher server-side
  // render_progress (via reconcileSeed's forward-progress rule) advances the
  // bar. Mounted once here, driven by the shared render-state store.
  useRenderPollingFallback(fetchClips);

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-400">Loading clips...</div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="bg-red-900/30 border border-red-700 rounded px-4 py-3 max-w-xs">
          <p className="text-sm text-red-200 mb-2">{fetchError}</p>
          <button
            onClick={fetchClips}
            className="text-xs font-semibold text-red-300 hover:text-red-100 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // The list is the working set, not every clip that ever existed: the backend
  // serves clips touched in the last 24 hours. Say where the rest went — an
  // archive nobody knows about reads as lost work. A requested clip that is not
  // here gets the same explanation, said once and in the attention colour.
  const archiveNote = (
    <p
      data-testid={focusMissing ? 'clip-focus-missing' : undefined}
      className={`text-xs shrink-0 ${focusMissing ? 'text-amber-200' : 'text-slate-500'}`}
    >
      {focusMissing ? 'That clip is not in the recent list — clips' : 'Clip prompts'} move to the{' '}
      <a href="/archive" className={`underline ${focusMissing ? 'hover:text-amber-100' : 'hover:text-slate-300'}`}>
        Archive
      </a>{' '}
      after 24 hours. Opening {focusMissing ? 'it' : 'one'} there brings it back here.
    </p>
  );

  if (clips.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center py-8 text-slate-500">
          <p>No clips yet. Generate some ideas to get started!</p>
          <div className="mt-2">{archiveNote}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full gap-4">
      <div className="flex items-center justify-between gap-4 shrink-0">
        <h2 className="text-base font-bold uppercase tracking-[0.2em] text-[var(--app-text)]">CLIPS ({clips.length})</h2>
        {focusMissing ? null : archiveNote}
      </div>

      {focusMissing ? archiveNote : null}

      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4 pr-2 motion-stagger">
          {clips.map((clip) => (
            <div
              key={clip.id}
              data-clip-id={clip.id}
              role="group"
              aria-label={clip.name || 'Clip'}
              tabIndex={-1}
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-strong)]"
            >
              <ClipPromptItem
                clip={clip}
                costSummary={costByClip[clip.id] ?? null}
                isExpanded={expandedId === clip.id}
                onToggleExpand={() => { void handleToggleExpand(clip); }}
                onRefresh={fetchClips}
                mediaProfile={mediaProfile}
                activeAccount={activeAccount}
                activeUser={activeUser}
                onOpenEditor={onOpenEditor}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClipPromptsList;
